import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { API, updatePageTitle } from '../../lib/appConfig';
import { useTheme } from '../../hooks/useTheme';

import { AdminLogin } from './AdminLogin';
import { Sidebar } from './Sidebar';
import { DashboardTab } from './DashboardTab';
import { AdvancedFilters } from './AdvancedFilters';
import { StandardReportTab } from './StandardReportTab';
import { LinkGeneratorTab } from './LinkGeneratorTab';
import { RoundHistoryTab } from './RoundHistoryTab';
import { SectorsTab } from './SectorsTab';
import { SuppressionNotice } from './SuppressionNotice';
import { ReportConfiguration } from './ReportConfiguration';
import { IndividualResponsesTable } from './IndividualResponsesTable';
import { DimensionChart } from './DimensionChart';
import { SettingsTab } from './SettingsTab';

// Admin Dashboard Component — Sidebar layout
export const AdminDashboard = () => {
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [results, setResults] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [individualResponses, setIndividualResponses] = useState([]);
  const [responsePagination, setResponsePagination] = useState({});
  const [groupBySession, setGroupBySession] = useState(true);
  const [filterOptions, setFilterOptions] = useState({ departments: [], company_times: [], tags: [] });
  const [filters, setFilters] = useState({ tag: '', departments: [], company_times: [] });
  const [loading, setLoading] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [tagStatus, setTagStatus] = useState([]);
  const [linkGenerator, setLinkGenerator] = useState({
    clientName: '', employeeCount: '', generatedLink: '', newRound: false, loading: false
  });

  const fetchFilterOptions = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/admin/filter-options`); setFilterOptions(r.data); }
    catch (e) { console.error(e); }
  }, [token]);

  const fetchTagStatus = useCallback(async () => {
    if (!token) return;
    try { const r = await axios.get(`${API}/admin/tag-status`); if (r.data.success) setTagStatus(r.data.tags || []); }
    catch (e) { console.error(e); }
  }, [token]);

  const fetchIndividualResponses = useCallback(async (page = 1) => {
    if (!token) return;
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: '50', group_by_session: groupBySession.toString() });
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.departments?.length) params.append('departments', filters.departments.join(','));
      if (filters.company_times?.length) params.append('company_times', filters.company_times.join(','));
      const response = await axios.get(`${API}/admin/individual-responses?${params}`);
      if (groupBySession) {
        setIndividualResponses([]);
        setResponsePagination({ ...response.data, sessions: response.data.sessions || [] });
      } else {
        setIndividualResponses(response.data.responses || []);
        setResponsePagination({ page: response.data.page, page_size: response.data.page_size, total: response.data.total, total_pages: response.data.total_pages });
      }
    } catch (e) { console.error(e); }
  }, [token, filters, groupBySession]);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.tag) params.append('tag', filters.tag);
      if (filters.departments?.length) params.append('departments', filters.departments.join(','));
      if (filters.company_times?.length) params.append('company_times', filters.company_times.join(','));
      const [resultsResp, chartResp] = await Promise.all([
        axios.get(`${API}/admin/results?${params}`),
        axios.get(`${API}/admin/chart-data?${params}`)
      ]);
      setResults(resultsResp.data);
      setChartData(chartResp.data.chart_data || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filters]);

  useEffect(() => {
    updatePageTitle('Dashboard Administrativo');
    if (token) { setIsAuthenticated(true); fetchFilterOptions(); fetchData(); fetchTagStatus(); }
  }, [token, fetchFilterOptions, fetchData, fetchTagStatus]);

  useEffect(() => {
    if (isAuthenticated) fetchData();
  }, [filters, isAuthenticated, fetchData]);

  // Sessão expirada (401 do backend): volta para a tela de login
  useEffect(() => {
    const onSessionExpired = () => {
      setToken(null); setIsAuthenticated(false); setResults(null);
      alert('Sessão expirada. Faça login novamente.');
    };
    window.addEventListener('admin-session-expired', onSessionExpired);
    return () => window.removeEventListener('admin-session-expired', onSessionExpired);
  }, []);

  const login = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, credentials);
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('admin_token', newToken);
      setIsAuthenticated(true);
    } catch { alert('Credenciais inválidas'); }
    setLoading(false);
  };

  const generateClientLink = async () => {
    if (!linkGenerator.clientName.trim()) { alert('Insira o nome do cliente'); return; }
    if (!linkGenerator.employeeCount || linkGenerator.employeeCount <= 0) { alert('Insira um número válido de funcionários'); return; }
    setLinkGenerator(prev => ({ ...prev, loading: true }));
    try {
      const response = await axios.post(`${API}/admin/generate-link`, {
        client_name: linkGenerator.clientName,
        employee_count: parseInt(linkGenerator.employeeCount),
        new_round: linkGenerator.newRound
      });
      if (response.data.success) {
        setLinkGenerator(prev => ({ ...prev, generatedLink: response.data.survey_link, roundLabel: response.data.round_label || '', loading: false }));
      }
    } catch (error) {
      alert(error.response?.data?.detail || 'Erro ao gerar link');
      setLinkGenerator(prev => ({ ...prev, loading: false }));
    }
  };

  const generateReport = async (config) => {
    setReportLoading(true);
    try {
      const response = await axios.post(`${API}/admin/generate-report`, {
        filters: { tag: filters.tag, departments: filters.departments, company_times: filters.company_times },
        include_fields: config.includeFields, group_by_session: config.groupBySession
      });
      if (response.data.success) {
        const link = document.createElement('a');
        link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${response.data.data}`;
        link.download = response.data.filename;
        link.click();
      }
    } catch { alert('Erro ao gerar relatório.'); }
    setReportLoading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      const n = document.createElement('div');
      n.textContent = 'Link copiado!';
      n.style.cssText = 'position:fixed;top:16px;right:16px;background:#00a74f;color:white;padding:10px 18px;border-radius:6px;font-size:13px;font-weight:600;z-index:9999;box-shadow:0 4px 12px rgba(0,0,0,.15)';
      document.body.appendChild(n);
      setTimeout(() => n.remove(), 2500);
    });
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null); setIsAuthenticated(false); setResults(null);
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'responses') fetchIndividualResponses(1);
  };

  if (!isAuthenticated) {
    return <AdminLogin credentials={credentials} setCredentials={setCredentials}
      login={login} loading={loading} isDarkMode={isDarkMode} toggleTheme={toggleTheme} />;
  }

  const contentMargin = sidebarCollapsed ? 'ml-16' : 'ml-56';

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-zinc-950' : 'bg-zinc-100'}`}
      style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
      data-testid="admin-dashboard">
      <Sidebar
        activeTab={activeTab}
        onTabChange={handleTabChange}
        isDarkMode={isDarkMode}
        toggleTheme={toggleTheme}
        onLogout={logout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <main className={`transition-all duration-200 ${contentMargin} p-6`}>
        {/* Page Header */}
        <div className="mb-6">
          <h1 className={`text-lg font-bold tracking-tight ${isDarkMode ? 'text-zinc-100' : 'text-zinc-900'}`}
            style={{ fontFamily: 'Manrope, sans-serif' }}>
            {activeTab === 'dashboard' && 'Visão Geral'}
            {activeTab === 'charts' && 'Gráficos'}
            {activeTab === 'responses' && 'Respostas Individuais'}
            {activeTab === 'standard-report' && 'Relatório Padronizado'}
            {activeTab === 'generator' && 'Gerar Links'}
            {activeTab === 'round-history' && 'Histórico de Rodadas'}
            {activeTab === 'sectors' && 'Setores'}
            {activeTab === 'settings' && 'Configurações'}
          </h1>
          <p className={`text-xs mt-0.5 ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
            Análise de Riscos Psicossociais — DEMoraes Consultoria
          </p>
        </div>

        {/* Filters (show on data tabs) */}
        {['dashboard', 'charts', 'responses'].includes(activeTab) && (
          <div className="mb-5">
            <AdvancedFilters
              filters={filters}
              setFilters={setFilters}
              filterOptions={filterOptions}
              tagStatus={tagStatus}
              isDarkMode={isDarkMode}
              onFetchResults={fetchData}
            />
          </div>
        )}

        {/* Loading */}
        {loading && ['dashboard', 'charts'].includes(activeTab) && (
          <div className={`flex items-center justify-center py-16 ${isDarkMode ? 'text-zinc-400' : 'text-zinc-600'}`}>
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-demoraes-green mr-3" />
            <span className="text-sm">Carregando dados...</span>
          </div>
        )}

        {/* Dashboard Tab */}
        {activeTab === 'dashboard' && !loading && (
          <DashboardTab results={results} filters={filters} isDarkMode={isDarkMode} />
        )}

        {/* Charts Tab */}
        {activeTab === 'charts' && !loading && (
          results?.suppressed ? (
            <SuppressionNotice
              totalSessions={results.total_sessions}
              minRespondents={results.min_segment_respondents}
              isDarkMode={isDarkMode}
            />
          ) : (
            <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
              <DimensionChart data={chartData} filters={filters} totalSessions={results?.total_sessions || 0} isDarkMode={isDarkMode} />
            </div>
          )
        )}

        {/* Responses Tab */}
        {activeTab === 'responses' && (
          <div className={`rounded-lg border p-6 ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <ReportConfiguration onGenerate={generateReport} loading={reportLoading} isDarkMode={isDarkMode} />
            <IndividualResponsesTable
              responses={individualResponses} loading={loading} pagination={responsePagination}
              onPageChange={(p) => fetchIndividualResponses(p)} groupBySession={groupBySession}
              isDarkMode={isDarkMode} onToggleGrouping={() => { setGroupBySession(!groupBySession); setTimeout(() => fetchIndividualResponses(1), 100); }}
            />
          </div>
        )}

        {/* Standard Report Tab */}
        {activeTab === 'standard-report' && (
          <StandardReportTab token={token} filters={filters} filterOptions={filterOptions} isDarkMode={isDarkMode} />
        )}

        {/* Generator Tab */}
        {activeTab === 'generator' && (
          <LinkGeneratorTab
            linkGenerator={linkGenerator} setLinkGenerator={setLinkGenerator}
            generateClientLink={generateClientLink} copyToClipboard={copyToClipboard}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Round History Tab */}
        {activeTab === 'round-history' && (
          <RoundHistoryTab token={token} isDarkMode={isDarkMode} />
        )}

        {/* Sectors Tab */}
        {activeTab === 'sectors' && (
          <SectorsTab token={token} isDarkMode={isDarkMode} availableTags={filterOptions.tags || []} />
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <SettingsTab isDarkMode={isDarkMode} />
        )}

        {/* No data fallback */}
        {!results && !loading && ['dashboard', 'charts'].includes(activeTab) && (
          <div className={`rounded-lg border p-12 text-center ${isDarkMode ? 'bg-zinc-900 border-zinc-800' : 'bg-white border-zinc-200'}`}>
            <p className={`text-sm ${isDarkMode ? 'text-zinc-500' : 'text-zinc-500'}`}>
              Nenhum resultado encontrado. Ajuste os filtros ou aguarde novas respostas.
            </p>
          </div>
        )}
      </main>
    </div>
  );
};
